import React, { JSX, AnchorHTMLAttributes, FC, Fragment } from 'react';

import {
  ARROW_POSITION_LEFT,
  ARROW_POSITION_RIGHT,
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
  ComponentSize,
  FontSize,
  FontWeight,
  IconPosition,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { getMappedColor, makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { IIcon, IIconCustomSize, VerticalAlign } from '../icons/icon';
import IconArrowLink from '../icons/icon-arrow-link';
import Text from '../text/text';

import './cta.scss';

export interface ICta extends IComponent, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  href?: string;
  Icon?: FC<IIcon>;
  iconPosition?: IconPosition;
  iconSize?: ComponentSize;
  isMobileButtonWhite?: boolean;
  mobileButton?: boolean;
  hasWrapper?: boolean;
  uppercase?: boolean;
  narrow?: boolean;
  inline?: boolean;
  target?: string;
  verticalAlign?: VerticalAlign;
  iconCustomSize?: IIconCustomSize;
  label?: string; // aria-label
}

// To be used as DEFAULTS from CALLING COMPONENTS
export const COLOR_WITH_SCHEMA_DARK: Color = 'light';
export const COLOR_WITH_SCHEMA_LIGHT: Color = 'red-light';
export const DEFAULT_COLOR_MOBILE_WHITE: Color = 'light';
export const DEFAULT_COLOR: Color = 'red-light';
export const DEFAULT_FONT_SIZE: FontSize = '14';
export const DEFAULT_FONT_WEIGHT: FontWeight = '500';
export const DEFAULT_ICON_ALIGNMENT: VerticalAlign = 'middle';
export const DEFAULT_ICON_SIZE: ComponentSize = 'xsmall';

const colorSchemaMapping = {
  [COLOR_SCHEMA_DARK]: COLOR_WITH_SCHEMA_DARK,
  [COLOR_SCHEMA_DARK_LIGHT]: COLOR_WITH_SCHEMA_DARK,
  [COLOR_SCHEMA_LIGHT]: COLOR_WITH_SCHEMA_LIGHT,
};

const hasLeft = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_LEFT;
const hasRight = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_RIGHT;

const classNames = ({
  mobileButton,
  isMobileButtonWhite,
  uppercase = true,
  narrow = false,
}: {
  mobileButton: boolean;
  isMobileButtonWhite: boolean;
  uppercase: boolean;
  narrow: boolean;
}) =>
  makeClassNames({
    cta: [
      'cta',
      { 'cta--mobile-button': mobileButton },
      { 'cta--mobile-button-white': isMobileButtonWhite },
      { 'cta--uppercase': uppercase },
      { 'cta--expanded': !narrow },
      { 'cta--inline': narrow },
    ],
    prepend: 'cta__prepend',
    append: 'cta__append',
  });

const Cta = ({
  /* istanbul ignore next: default value */ color = DEFAULT_COLOR,
  /* istanbul ignore next: default value */ fontSize = DEFAULT_FONT_SIZE,
  /* istanbul ignore next: default value */ fontWeight = DEFAULT_FONT_WEIGHT,
  /* istanbul ignore next: default value */ hasWrapper = true,
  /* istanbul ignore next: default value */ href = '',
  /* istanbul ignore next: default value */ Icon = IconArrowLink,
  /* istanbul ignore next: default value */ iconSize = DEFAULT_ICON_SIZE,
  /* istanbul ignore next: default value */ isMobileButtonWhite = false,
  /* istanbul ignore next: default value */ mobileButton = false,
  /* istanbul ignore next: default value */ trackingData = {},
  /* istanbul ignore next: default value */ verticalAlign = DEFAULT_ICON_ALIGNMENT,
  /* istanbul ignore next: default value */ uppercase = true,
  /* istanbul ignore next: default value */ narrow = false,
  /* istanbul ignore next: default value */ inline = false,
  children,
  className,
  colorSchema,
  iconPosition,
  onClick,
  target,
  iconCustomSize,
  label,
  ...attr
}: ICta) => {
  // WARNING: colorSchema if setted always win on prop color
  const colorToUse: Color | undefined = isMobileButtonWhite
    ? DEFAULT_COLOR_MOBILE_WHITE
    : getMappedColor({ colorSchema, colorSchemaMapping, defaultColor: color });
  const cn = classNames({ mobileButton, isMobileButtonWhite, uppercase, narrow });

  const arrowLeft: JSX.Element | null =
    iconPosition && hasLeft(iconPosition) ? (
      <span className={cn.prepend}>
        <Icon customSize={iconCustomSize} size={iconSize} color={colorToUse} reverse verticalAlign={verticalAlign} />
      </span>
    ) : null;

  const arrowRight: JSX.Element | null =
    iconPosition && hasRight(iconPosition) ? (
      <span className={cn.append}>
        <Icon customSize={iconCustomSize} size={iconSize} color={colorToUse} verticalAlign={verticalAlign} />
      </span>
    ) : null;

  const ctaText: JSX.Element = hasWrapper ? (
    <Text fontWeight={fontWeight} fontSize={fontSize} color={colorToUse}>
      {children}
    </Text>
  ) : (
    <Fragment>{children}</Fragment>
  );

  return (
    <a
      href={href}
      onClick={onClick}
      className={[cn.cta, className].filter(Boolean).join(' ')}
      {...objectToDataAttributes(trackingData)}
      target={target}
      aria-label={label}
      {...attr}
    >
      {arrowLeft}
      {ctaText}
      {arrowRight}
    </a>
  );
};

export default Cta;
